import { Close, Edit, TableRows } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SolaForceHeading } from '~/components/SolaForceHeading';
import { EmpPosition } from '~/data/employee';
import { EnumListKey } from '~/data/enums';
import { useEnumFilteredOptions } from '~/features/OverlappingEmploymentDialog/hooks/useEnumFilteredOption';
import { OverlappingEmploymentFormValues } from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { useSxStyles } from '../useStyles';

interface Props {
  empId: number;
  empPosition: EmpPosition;
  formValues: OverlappingEmploymentFormValues;
}

export const WorkingHoursForm = ({ formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    formProps: { control, setValue },
  } = formValues;

  const [
    fullTimeHours,
    partTimeHours,
    defaultStartDate,
    workingHoursStartDate,
  ] = useWatch({
    control,
    name: [
      'overlappingEmployment.currentWorkingHours.fWorkHoursFtHoursPerWeek',
      'overlappingEmployment.currentWorkingHours.fWorkHoursPtHoursPerWeek',
      'overlappingEmployment.basicData.fEmplDateStart',
      'overlappingEmployment.currentWorkingHours.fWorkHoursStartDate',
    ],
  });

  const [showRow, setShowRow] = useState(!!workingHoursStartDate);

  useEffect(() => {
    if (!workingHoursStartDate)
      setValue(
        'overlappingEmployment.currentWorkingHours.fWorkHoursStartDate',
        defaultStartDate,
      );
  }, [defaultStartDate, setValue, workingHoursStartDate]);

  useEffect(() => {
    if (fullTimeHours && partTimeHours) {
      const percentage = (+partTimeHours / +fullTimeHours) * 100;
      setValue(
        'overlappingEmployment.currentWorkingHours.fWorkHoursPtPercentage',
        percentage,
      );
    }
  }, [fullTimeHours, partTimeHours, setValue]);

  const [filteredOptionsWorkingHoursType] = useEnumFilteredOptions(control, [
    EnumListKey.WORKING_HOURS_TYPE,
  ]);

  const dateFormat = 'YYYY-MM-DD';
  const requiredText = t('fieldIsRequired');

  return (
    <Box>
      <SolaForceHeading
        headingIcon={<TableRows />}
        heading={t('laCurrentWorkingHours')}
      >
        {!showRow ? (
          <Button onClick={() => setShowRow(true)} startIcon={<Edit />}>
            {t('laEdit')}
          </Button>
        ) : (
          <Button onClick={() => setShowRow(false)} startIcon={<Close />}>
            {t('laCancel')}
          </Button>
        )}
      </SolaForceHeading>
      {showRow && (
        <Grid sx={{ overflowX: 'auto' }}>
          <Box sx={sxStyles.table} component={'table'}>
            <Box component={'thead'}>
              <Box component={'tr'}>
                <Box component={'th'}>{t('laHoursWeek')}</Box>
                <Box component={'th'}>{t('laPart-time')}</Box>
                <Box component={'th'}>{t('laPart-timeRatio')}</Box>
                <Box component={'th'}>
                  {t('laStartDate')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laEndDate')}</Box>
                <Box component={'th'}>{t('laPart-timeReason')}</Box>
                <Box component={'th'}>{t('laWorkingHoursType')}</Box>
                <Box component={'th'}>{t('laInfo')}</Box>
                <Box component={'th'}></Box>
              </Box>
            </Box>
            <Box component={'tbody'}>
              <Box component={'tr'}>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursFtHoursPerWeek'
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        type="number"
                        value={value}
                        onChange={onChange}
                        size="small"
                        sx={sxStyles.inputField}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursPtHoursPerWeek'
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        type="number"
                        value={value}
                        onChange={onChange}
                        size="small"
                        sx={sxStyles.inputField}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursPtPercentage'
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        type="number"
                        value={value}
                        onChange={onChange}
                        size="small"
                        sx={sxStyles.inputField}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursStartDate'
                    }
                    control={control}
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={convertToMomentOrNull(
                          value ? value : defaultStartDate,
                        )}
                        onChange={(v: Moment | null) => {
                          onChange(v?.format(dateFormat));
                        }}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!error,
                            helperText: error?.message,
                            size: 'small',
                            sx: sxStyles.inputField,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursEndDate'
                    }
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={convertToMomentOrNull(value)}
                        onChange={(v: Moment | null) => {
                          onChange(v?.format(dateFormat));
                        }}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!error,
                            helperText: error?.message,
                            size: 'small',
                            sx: sxStyles.inputField,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursPtReason'
                    }
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        size="small"
                        sx={sxStyles.inputField}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursType'
                    }
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={filteredOptionsWorkingHoursType}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code || '')}
                        value={
                          filteredOptionsWorkingHoursType.find(
                            (o) => o?.code === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            sx={sxStyles.inputField}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.currentWorkingHours.fWorkHoursInfo'
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        value={value}
                        multiline
                        rows={1}
                        size="small"
                        onChange={onChange}
                        sx={sxStyles.inputField}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <IconButton size="small" sx={sxStyles.deleteButton}>
                    <Box component={'i'} className="fa fa-trash"></Box>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      )}
    </Box>
  );
};
